import { Box, Text, ThemeContext, Anchor } from 'grommet';
import React, { useContext, useState } from 'react';
import useSWR from 'swr';
import { INVOICE_CARD_MIN_WIDTH } from '../constants/Dimensions';
import { silentFetcher } from '../utils/errors';
import { DarkenOnHoverBox, DisplayInlineBlockBox, EnhancedDropButton } from '../utils/styled';
import ContentLine from './ContentLine';
import { InfoLine } from './CustomerTag';
import { formatCurrency } from '../utils/numbers';
import { formatDate } from '../utils/dates';
import { useRouter } from 'next/router';
import {
    faUser as IconMan,
    faCalendar as IconCalendar,
    faFileInvoiceDollar as IconFileInvoice,
    faCalendarExclamation as IconCalendarDue,
    faXmarkLarge as IconClose,
    faBagShopping as IconShopping
} from '@fortawesome/pro-light-svg-icons';
import FontAwesomeIcon from './FontAwesomeIcon';
import Badge from './Badge';
import { formatName } from '../utils/names';
import { getFormattedPeriodLabel, getFrequencyLabel } from './pages/draft-invoices/edit/RecurringScheduleForm';
import { renderPOnumber } from '../utils/invoice';
import Button from './Button';

const OrderTag = ({id, onRemove = undefined}) => {
    const theme = useContext(ThemeContext);
    const router = useRouter();
    const [isRecurringInvoice, setIsRecurringInvoice] = useState(false);
    const [isMilestoneInvoice, setIsMilestoneInvoice] = useState(false);

    const { data } = useSWR(
        `/api/draft-invoices/${id}`,
        silentFetcher,
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000, // 1 minute - reduce duplicate requests
            onSuccess: (orderData) => {
                setIsRecurringInvoice(orderData && !!orderData?.draft_series_id);
                setIsMilestoneInvoice(orderData && orderData?.billing_milestones && !!orderData?.billing_milestones.length);
            }
        },
    );

    return (
        <DisplayInlineBlockBox>
            <EnhancedDropButton
                event={{section: 'order_tag', element: 'tag'}}
                label={(
                    <DarkenOnHoverBox
                        background={theme.dark ? "foreground" : "light-1"}
                        border={{color: "border"}}
                        round="xxsmall"
                        theme={theme}>
                        <Box direction="row" align="center" pad="xxsmall">
                            <Box pad={{horizontal: 'xxsmall'}} direction="row" align="center" gap="xsmall">
                                <Text wordBreak="keep-all">
                                    {'#'}
                                    {data && (data?.draft_series_id ? data?.series_identifier : data?.identifier)?.replace(/^#/, '')}
                                </Text>
                                {!data && (<ContentLine width="xxsmall" isLoading={true} />)}
                            </Box>
                            {onRemove && (
                                <Box pad={{left: 'xxsmall', right: 'xsmall'}}>
                                    <Anchor
                                        plain
                                        hoverIndicator
                                        label={(<FontAwesomeIcon SelectedIcon={IconClose} size="xxxsmall" />)}
                                        onClick={() => onRemove(id)} />
                                </Box>
                            )}
                        </Box>
                    </DarkenOnHoverBox>
                )}
                plain
                hoverIndicator
                dropAlign={{top: 'bottom', left: 'left'}}
                dropProps={{border: true, margin: {vertical: 'xsmall'}}}
                dropContent={data ? (
                    <Box pad="xsmall" margin="small" width={{max: 'medium', min: INVOICE_CARD_MIN_WIDTH}} flex={{shrink: 0}}>
                        <Box margin={{vertical: 'xsmall'}} border={{side: 'bottom'}} pad={{bottom: 'small'}}>
                            <Text size="large" weight={500} wordBreak="keep-all">
                                Invoice {data?.draft_series_id ? data?.series_identifier : data?.identifier}
                            </Text>
                            <Box pad={{vertical: 'xsmall'}}>
                                <Badge>{data?.scheduled ? 'Scheduled' : 'Draft'}</Badge>
                            </Box>
                        </Box>
                        <Box direction="column" gap="small" pad={{vertical: 'small'}}>
                            {renderPOnumber(data?.po_number) ? (
                                <InfoLine
                                    icon={IconShopping}
                                    description={`PO #${renderPOnumber(data.po_number)}`} />
                            ) : ''}
                            {data?.recipient_id ? (<InfoLine
                                icon={IconMan}
                                description={(<Anchor href={`/customers/${data?.recipient?.uuid || data?.recipient_uuid}`}>
                                    {data?.recipient?.company_name || formatName(data?.recipient, true)}
                                </Anchor>)} />) : ''}
                            {data?.total_amount ? (<InfoLine
                                icon={IconFileInvoice}
                                description={(<Text>{formatCurrency(data?.total_amount)} invoice total</Text>)} />) : ''}
                            {isRecurringInvoice? (<InfoLine
                                icon={IconCalendar}
                                description={(<Text>Sends {getFrequencyLabel(data?.draft_series, true, true)}{getFormattedPeriodLabel(data?.draft_series) ? ` on ${getFormattedPeriodLabel(data?.draft_series)}` : ''}</Text>)} />) : ''}
                            {isMilestoneInvoice ? (<InfoLine
                                icon={IconCalendar}
                                description={(
                                    <Text>
                                        {data?.billing_milestones.length} payments
                                    </Text>
                                )} />) : ''}
                            {data?.issued_at && !isRecurringInvoice && !isMilestoneInvoice ? (<InfoLine
                                icon={IconCalendar}
                                description={(<Text>{data?.scheduled ? "Scheduled to be issued on" : "Issued on"} {formatDate(data?.issued_at) || ''}</Text>)} />) : ''}
                            {data?.due_date && !isRecurringInvoice && !isMilestoneInvoice ? (<InfoLine
                                icon={IconCalendarDue}
                                description={(<Text>Due on {formatDate(data?.due_date) || ''}</Text>)} />) : ''}
                        </Box>
                        <Box margin={{top: 'small'}}>
                            <Button
                                event={{section: 'order_tag', element: 'view_draft_invoice', description: data?.id}}
                                primary
                                ctaColor="accent"
                                color="accent"
                                fill="horizontal"
                                label={(
                                    <Box direction="row" gap="small" align="center" fill="horizontal" justify="center">
                                        <Text color="text">View draft invoice</Text>
                                    </Box>
                                )}
                                onClick={() => router.push(`/draft-invoices/${data?.id}`)}
                            />
                        </Box>
                    </Box>
                ) : (<></>)}>
            </EnhancedDropButton>
        </DisplayInlineBlockBox>
    );
}

export default OrderTag;